import { PropsWithChildren, HTMLAttributes, useMemo } from 'react';

import { MakeOptional, PropsWithClassName, KeysOf } from '@/types';
import { cn } from '@/utils';
import { PLAYER_STATUS_COLORS } from '@/utils/constants';

type PLAYER_STATUS_ID = KeysOf<typeof PLAYER_STATUS_COLORS>;

type TagsProps = PropsWithChildren<{
  statusId: number;
  isUppercase?: boolean;
  textClassName?: string;
  className?: string;
}> &
  HTMLAttributes<HTMLDivElement>;

type ActionTagProps = PropsWithClassName<
  PropsWithChildren<{
    onClick: (id?: number) => void;
    classNameText?: PropsWithClassName['className'];
  }>
>;

type CheckboxTagProps = PropsWithClassName<
  PropsWithChildren<
    ActionTagProps & {
      isChecked: boolean;
      disabled?: boolean;
      id?: number;
    }
  >
>;

const AlertTag = ({
  children,
  statusId,
  isUppercase = true,
  textClassName,
  className,
  ...rest
}: TagsProps) => {
  const colors = useMemo(() => {
    return (
      PLAYER_STATUS_COLORS[statusId as PLAYER_STATUS_ID] ??
      PLAYER_STATUS_COLORS[201]
    );
  }, [statusId]);

  const tagClass = cn(
    'inline-block rounded-[6px] px-[8px] py-[2px] leading-[0px] whitespace-nowrap',
    className
  );

  return (
    <div
      className={tagClass}
      role="status"
      {...rest}
      style={{
        backgroundColor: colors.bgColor,
        color: colors.textColor,
      }}
    >
      <span
        className={cn(
          'font-bold text-xs',
          {
            uppercase: isUppercase,
          },
          textClassName
        )}
      >
        {children}
      </span>
    </div>
  );
};

const ActionTag = ({
  children,
  onClick,
  className,
  classNameText,
}: MakeOptional<ActionTagProps, 'onClick'>) => {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-[15px] h-[2.125rem] rounded-[8px] bg-gdt-blue-xl2',
        {
          'py-[7px]': !onClick,
        },
        className
      )}
      role="status"
    >
      <span
        className={cn(
          'inline-block text-gdt-blue text-xs font-semibold',
          classNameText
        )}
      >
        {children}
      </span>
      {onClick && (
        <button
          aria-label={`Cerrar Tag ${children}`}
          className="py-[5px] rounded-full border border-transparent focus-visible:outline-none focus-visible:border-gdt-blue"
          type="button"
          onClick={() => onClick()}
        >
          <svg
            fill="none"
            height="8"
            viewBox="0 0 8 8"
            width="8"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_9842_944)">
              <path
                d="M7 1L1 7"
                stroke="#3366FF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
              />
              <path
                d="M1 1L7 7"
                stroke="#3366FF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
              />
            </g>
          </svg>
        </button>
      )}
    </div>
  );
};

const CheckboxTag = ({
  isChecked,
  children,
  onClick,
  className,
  disabled,
  id,
}: CheckboxTagProps) => {
  return (
    <button
      key={id}
      aria-checked={isChecked ? 'true' : 'false'}
      className={cn(
        'px-[20px] rounded-[7px] border border-gdt-gray-4 text-center bg-white h-[30px] disabled:opacity-50 disabled:cursor-not-allowed text-gdt-gray-4 text-2xs font-semibold',
        {
          '!border-gdt-blue !bg-gdt-blue-xl2 !text-gdt-blue': isChecked,
        },
        className
      )}
      disabled={disabled}
      role="checkbox"
      type="button"
      onClick={() => {
        isChecked ? onClick(undefined) : onClick(id);
      }}
    >
      {children}
    </button>
  );
};

const Tags = {
  Alert: AlertTag,
  Action: ActionTag,
  Checkbox: CheckboxTag,
};

export default Tags;
